import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Badge from '../UI/Badge';
import Modal from '../UI/Modal';
import MaterialUpload from './MaterialUpload';

export default function CoursePage({ userRole }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [activeTab, setActiveTab] = useState('materials'); // materials, assignments, quizzes

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      const [courseRes, materialsRes, assignmentsRes, quizzesRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/courses/${courseId}/materials`),
        api.get(`/assignments/course/${courseId}`),
        api.get(`/quizzes/course/${courseId}`)
      ]);

      setCourse(courseRes.data.course);
      setMaterials(materialsRes.data.materials || []);
      setAssignments(assignmentsRes.data.assignments || []);
      setQuizzes(quizzesRes.data.quizzes || []);
    } catch (err) {
      setError(err.response?.data?.error || '수업 정보를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialUpload = () => {
    setShowUploadModal(false);
    fetchCourseData(); // Refresh materials
  };

  const getMaterialIcon = (type) => {
    const icons = {
      PDF: '📄',
      TEXT: '📝',
      LINK: '🔗',
      VIDEO: '🎥',
      IMAGE: '🖼️',
    };
    return icons[type] || '📎';
  };

  const handleMaterialClick = (material) => {
    if (material.fileUrl) {
      window.open(material.fileUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <Card className="text-center py-8">
        <p className="text-danger-500 mb-4">{error || '수업을 찾을 수 없습니다.'}</p>
        <Button onClick={() => navigate('/courses')}>수업 목록으로</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Course Header */}
      <Card>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-neutral-800">{course.title}</h1>
              {course.isActive ? (
                <Badge variant="success">진행중</Badge>
              ) : (
                <Badge variant="default">종료</Badge>
              )}
            </div>
            {course.description && (
              <p className="text-neutral-600">{course.description}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-neutral-100">
          <div>
            <p className="text-sm text-neutral-500">수업 코드</p>
            <p className="font-medium text-neutral-800">{course.courseCode}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-500">담당 교사</p>
            <p className="font-medium text-neutral-800">
              {course.teacher?.username || '알 수 없음'}
            </p>
          </div>
          {userRole === 'TEACHER' && course._count && (
            <div>
              <p className="text-sm text-neutral-500">수강 학생</p>
              <p className="font-medium text-neutral-800">
                {course._count.enrollments || 0}명
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-neutral-200">
        <button
          onClick={() => setActiveTab('materials')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'materials'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
        >
          학습 자료
        </button>
        <button
          onClick={() => setActiveTab('assignments')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'assignments'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
        >
          과제
        </button>
        <button
          onClick={() => setActiveTab('quizzes')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'quizzes'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
        >
          퀴즈
        </button>
      </div>

      {/* Materials Tab */}
      {activeTab === 'materials' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-neutral-800">학습 자료</h2>
            {userRole === 'TEACHER' && (
              <Button onClick={() => setShowUploadModal(true)}>
                자료 업로드
              </Button>
            )}
          </div>

          {materials.length === 0 ? (
            <Card className="text-center py-12">
              <p className="text-neutral-500 mb-4">아직 업로드된 자료가 없습니다.</p>
              {userRole === 'TEACHER' && (
                <Button onClick={() => setShowUploadModal(true)}>
                  첫 자료 업로드하기
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {materials.map((material) => (
                <Card
                  key={material.id}
                  hover
                  className="cursor-pointer"
                  onClick={() => handleMaterialClick(material)}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{getMaterialIcon(material.materialType)}</span>
                    <div className="flex-1">
                      <h3 className="font-medium text-neutral-800">{material.title}</h3>
                      {material.description && (
                        <p className="text-sm text-neutral-600 mt-1">{material.description}</p>
                      )}
                    </div>
                    <Badge variant="default">{material.materialType}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assignments Tab */}
      {activeTab === 'assignments' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-neutral-800">과제</h2>
            {userRole === 'TEACHER' && (
              <Button onClick={() => navigate(`/courses/${courseId}/assignments/create`)}>
                과제 만들기
              </Button>
            )}
          </div>

          {assignments.length === 0 ? (
            <Card className="text-center py-12">
              <p className="text-neutral-500 mb-4">아직 등록된 과제가 없습니다.</p>
              {userRole === 'TEACHER' && (
                <Button onClick={() => navigate(`/courses/${courseId}/assignments/create`)}>
                  첫 과제 만들기
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {assignments.map((assignment) => (
                <Card
                  key={assignment.id}
                  hover
                  className="cursor-pointer"
                  onClick={() => navigate(`/assignments/${assignment.id}`)}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">📄</span>
                    <div className="flex-1">
                      <h3 className="font-medium text-neutral-800">{assignment.title}</h3>
                      {assignment.description && (
                        <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{assignment.description}</p>
                      )}
                      <div className="flex gap-3 mt-2 text-xs text-neutral-500">
                        {assignment.dueDate && (
                          <span>마감: {new Date(assignment.dueDate).toLocaleDateString('ko-KR')}</span>
                        )}
                        <span>배점: {assignment.maxScore}점</span>
                      </div>
                    </div>
                    <Badge variant="info">과제</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quizzes Tab */}
      {activeTab === 'quizzes' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-neutral-800">퀴즈</h2>
            {userRole === 'TEACHER' && (
              <Button onClick={() => navigate(`/courses/${courseId}/quizzes/create`)}>
                퀴즈 만들기
              </Button>
            )}
          </div>

          {quizzes.length === 0 ? (
            <Card className="text-center py-12">
              <p className="text-neutral-500 mb-4">아직 등록된 퀴즈가 없습니다.</p>
              {userRole === 'TEACHER' && (
                <Button onClick={() => navigate(`/courses/${courseId}/quizzes/create`)}>
                  첫 퀴즈 만들기
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {quizzes.map((quiz) => (
                <Card
                  key={quiz.id}
                  hover
                  className="cursor-pointer"
                  onClick={() => navigate(`/quizzes/${quiz.id}`)}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">📝</span>
                    <div className="flex-1">
                      <h3 className="font-medium text-neutral-800">{quiz.title}</h3>
                      {quiz.description && (
                        <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{quiz.description}</p>
                      )}
                      <div className="flex gap-3 mt-2 text-xs text-neutral-500">
                        <span>문제 수: {quiz.questions?.length || 0}개</span>
                        {quiz.timeLimit && (
                          <span>시간 제한: {quiz.timeLimit}분</span>
                        )}
                      </div>
                    </div>
                    <Badge variant="success">퀴즈</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Material Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="학습 자료 업로드"
        size="md"
      >
        <MaterialUpload
          courseId={courseId}
          onSuccess={handleMaterialUpload}
          onCancel={() => setShowUploadModal(false)}
        />
      </Modal>
    </div>
  );
}
